import { useRef, useState } from 'react';
import {
	useCheckClosedMemberDuplicationMutation,
	useCheckMemberDuplicationMutation,
} from '@/api/member-management/memberDuplication';
import {
	ClosedMemberDuplicationCheckDTO,
	MemberDuplicationCheckDTO,
	MemberDuplicationCheckRequest,
} from '@/api/DTO/member-management/memberDuplication';
import InProgressDuplicateDialog from './InProgressDuplicateDialog';
import ClosedMemberDuplicateDialog, {
	ClosedMemberChoice,
} from './ClosedMemberDuplicateDialog';

export interface DuplicationCheckResult {
	/** `false` when staff cancelled, or a check API failed (error already shown). */
	proceed: boolean;
	/** Set when staff chose to link a service-closed member. */
	closedUserId?: string;
	closedMember?: ClosedMemberDuplicationCheckDTO;
}

interface RunOptions {
	/** Skip the service-closed step, e.g. the record is already linked. */
	skipClosedCheck?: boolean;
	/** Ask to confirm the picked closed member (default `true`). */
	confirmLink?: boolean;
}

/**
 * Member duplication check (DMS-REQ-WEB-CHA-002) shared by the three check points:
 * 1. `check-duplication` → in-progress hits must be confirmed "not the same person";
 * 2. `check-closed-duplication` → staff may pick a service-closed member to link.
 *
 * Render `duplicationDialogs` once, then `await runDuplicationCheck(body)` before
 * the action. Linking itself is left to the caller: send `closedUserId` in the
 * body of the action (create, Submit to IC, New register).
 */
export function useMemberDuplicationCheck() {
	const [checkDuplication] = useCheckMemberDuplicationMutation();
	const [checkClosedDuplication] = useCheckClosedMemberDuplicationMutation();

	const [inProgressRows, setInProgressRows] = useState<
		MemberDuplicationCheckDTO[] | null
	>(null);
	const [closedRows, setClosedRows] = useState<
		ClosedMemberDuplicationCheckDTO[] | null
	>(null);
	const [confirmLink, setConfirmLink] = useState(true);
	const confirmResolver = useRef<(confirmed: boolean) => void>(undefined);
	const closedResolver =
		useRef<(choice: ClosedMemberChoice) => void>(undefined);

	const runDuplicationCheck = async (
		body: MemberDuplicationCheckRequest,
		options: RunOptions = {}
	): Promise<DuplicationCheckResult> => {
		const response = await checkDuplication(body);
		if (!('data' in response) || !response.data?.success) {
			return { proceed: false };
		}
		const hits = response.data.data ?? [];
		if (hits.length > 0) {
			const confirmed = await new Promise<boolean>((resolve) => {
				confirmResolver.current = resolve;
				setInProgressRows(hits);
			});
			setInProgressRows(null);
			if (!confirmed) return { proceed: false };
		}

		if (options.skipClosedCheck) return { proceed: true };

		const closedResponse = await checkClosedDuplication(body);
		if (!('data' in closedResponse) || !closedResponse.data?.success) {
			return { proceed: false };
		}
		const closedHits = closedResponse.data.data ?? [];
		if (closedHits.length === 0) return { proceed: true };

		const choice = await new Promise<ClosedMemberChoice>((resolve) => {
			closedResolver.current = resolve;
			setConfirmLink(options.confirmLink ?? true);
			setClosedRows(closedHits);
		});
		setClosedRows(null);
		if (choice.action === 'cancel') return { proceed: false };
		if (choice.action === 'link') {
			return {
				proceed: true,
				closedUserId: choice.closedMember.id,
				closedMember: choice.closedMember,
			};
		}
		return { proceed: true };
	};

	const duplicationDialogs = (
		<>
			<InProgressDuplicateDialog
				open={!!inProgressRows}
				rows={inProgressRows ?? []}
				onClose={(confirmed) => confirmResolver.current?.(confirmed)}
			/>
			<ClosedMemberDuplicateDialog
				open={!!closedRows}
				rows={closedRows ?? []}
				confirmLink={confirmLink}
				onClose={(choice) => closedResolver.current?.(choice)}
			/>
		</>
	);

	return { runDuplicationCheck, duplicationDialogs };
}
